/* ==========================================================================
   EXAM ENGINE — TakeMyOnlineClass.store
   --------------------------------------------------------------------------
   Vanilla JS, no dependencies, no build step. Runs inside Shopify's Liquid
   output. Depends on, in load order:
     exam-catalog.js    XP_CATALOG, XP_DIFFICULTIES, XP_LIMITS
     exam-bank.js       XP_BANK
     exam-generator.js  XP_GEN
   and optionally window.XP_COURSES, injected by the Liquid section from the
   store's product list.
   ========================================================================== */

(function () {
  'use strict';

  /* The engine is also referenced from theme.liquid for the order bar. If it
     ever loads twice on this page, two instances fight over the same DOM and
     the second wipes the first. One instance only. */
  if (window.__NQP_ENGINE_LOADED__) return;
  window.__NQP_ENGINE_LOADED__ = true;

  var CAT = window.XP_CATALOG || [];
  var BANK = (window.XP_BANK && window.XP_BANK.items) || [];
  var GEN = window.XP_GEN;
  var LIM = window.XP_LIMITS || { freeMax: 400, premiumMax: 2000, premiumPrice: 12 };
  var DIFFS = window.XP_DIFFICULTIES || [];

  /* ---------------------------------------------------------------------
     Storage — namespaced, fails silently in private browsing
     --------------------------------------------------------------------- */
  var KEY = 'tmoc_xp_v1';
  var store = {
    read: function () {
      try { return JSON.parse(localStorage.getItem(KEY)) || {}; }
      catch (e) { return {}; }
    },
    write: function (o) {
      try { localStorage.setItem(KEY, JSON.stringify(o)); return true; }
      catch (e) { return false; }
    },
    patch: function (fn) { var s = this.read(); fn(s); this.write(s); return s; }
  };

  function state() {
    var s = store.read();
    s.history = s.history || [];        // completed sittings
    s.missed = s.missed || {};          // itemId -> { misses, lastSeen, exam }
    s.streak = s.streak || { count: 0, last: null };
    s.unlocks = s.unlocks || {};        // examId -> { code, granted, cap }
    s.resume = s.resume || null;        // in-flight session
    return s;
  }

  /* ---------------------------------------------------------------------
     Small helpers
     --------------------------------------------------------------------- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html !== undefined) n.innerHTML = html;
    return n;
  }
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function param(name) {
    return new URLSearchParams(window.location.search).get(name);
  }
  function fmtTime(sec) {
    if (sec < 0) sec = 0;
    var h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    var pad = function (n) { return n < 10 ? '0' + n : String(n); };
    return (h > 0 ? h + ':' : '') + pad(m) + ':' + pad(s);
  }
  function shuffle(arr, seedFn) {
    var a = arr.slice(), rnd = seedFn || Math.random;
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(rnd() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }
  function toast(msg, ms) {
    var t = $('#xp-toast');
    if (!t) { t = el('div', 'xp-toast'); t.id = 'xp-toast'; document.body.appendChild(t); }
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._h);
    t._h = setTimeout(function () { t.classList.remove('is-visible'); }, ms || 2600);
  }
  /* Legacy ids from links published before the RN/PN split. */
  var ID_ALIAS = {
    'nclex': 'nclex-rn',
    'ati-med-surg': 'ati-med-surg-rn'
  };

  function findExam(id) {
    if (ID_ALIAS[id]) id = ID_ALIAS[id];
    for (var i = 0; i < CAT.length; i++) if (CAT[i].id === id) return CAT[i];
    var courses = window.XP_COURSES || [];
    for (var j = 0; j < courses.length; j++) if (courses[j].id === id) return courses[j];
    return null;
  }

  /* ---------------------------------------------------------------------
     Course tracks built from the Shopify product list
     Every product in the store automatically gets a midterm and a final.
     --------------------------------------------------------------------- */
  function buildCourseTracks() {
    var raw = window.XP_COURSES_RAW || [];
    var out = [];
    raw.forEach(function (p) {
      /* Subject classification by course-code prefix, then title keywords.
         The old regex only knew maths and nursing words, so a course like
         MIS561 (Management Information Systems) fell through to "general"
         and was served algebra. */
      var SUB = window.XP_SUBJECTS;
      var subject = SUB ? SUB.classify(p.title, p.tags, p.type) : 'general';
      var kind = subject === 'nursing' ? 'clinical'
        : (subject === 'math' || subject === 'statistics' || subject === 'accounting'
           || subject === 'finance' || subject === 'physics') ? 'quant' : 'general';
      var subjSections = SUB ? SUB.sectionsFor(subject) : null;
      var subjLabel = SUB ? SUB.labelFor(subject) : 'General Studies';

      ['midterm', 'final'].forEach(function (form) {
        out.push({
          id: 'course-' + p.handle + '-' + form,
          name: p.title + ' — ' + (form === 'midterm' ? 'Midterm' : 'Final') + ' Exam',
          shortName: p.title,
          icon: form === 'midterm' ? '📘' : '📕',
          group: 'course',
          form: form,
          kind: kind,
          subject: subject,
          subjectLabel: subjLabel,
          productHandle: p.handle,
          productUrl: p.url || ('/products/' + p.handle),
          science: kind === 'clinical' || subject === 'biology' || subject === 'chemistry' || subject === 'physics',
          blurb: (form === 'midterm'
            ? 'Midterm practice for ' + p.title + ' \u2014 ' + subjLabel + '.'
            : 'Cumulative final practice for ' + p.title + ' \u2014 ' + subjLabel + '.'),
          minutes: form === 'midterm' ? 90 : 150,
          fullLen: form === 'midterm' ? 50 : 100,
          pass: 70,
          topics: (p.tags || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean).slice(0, 8),
          sections: subjSections || [
            { id: 'concepts', name: 'Core Concepts', weight: 30 },
            { id: 'applied', name: 'Applied Problems', weight: 40 },
            { id: 'analysis', name: 'Analysis & Synthesis', weight: 30 }
          ]
        });
      });
    });
    window.XP_COURSES = out;
    return out;
  }

  /* ---------------------------------------------------------------------
     Item supply — curated first, generated to fill
     --------------------------------------------------------------------- */
  /* PN tracks reuse the RN track's purely clinical items — physiology does not
     change with licence level. Scope-of-practice, delegation and management
     items are NOT shared, because the correct answer genuinely differs. */
  var SHARE_FROM = { 'nclex-pn': 'nclex-rn', 'ati-med-surg-pn': 'ati-med-surg-rn' };
  var NOT_SHARED_SECTIONS = { 'safe-care': 1, 'coordinated-care': 1, 'scope': 1, 'safety-infection': 1 };

  function curatedFor(examId, sections, difficulty) {
    var donor = SHARE_FROM[examId];
    return BANK.filter(function (it) {
      var ok = (it.exam === examId);
      if (!ok && donor && it.exam === donor && !NOT_SHARED_SECTIONS[it.section]) ok = true;
      if (!ok) return false;
      if (sections && sections.length && sections.indexOf(it.section) === -1) return false;
      if (difficulty && difficulty !== 'combined' && it.difficulty !== difficulty) return false;
      return true;
    });
  }

  function assemblePaper(cfg) {
    var exam = cfg.exam;
    var want = cfg.count;
    var seedFn = GEN ? GEN.rng(cfg.seed) : Math.random;

    var pool = curatedFor(exam.id, cfg.sections, cfg.difficulty);

    /* Weighted-by-blueprint ordering when the whole exam is in scope */
    pool = shuffle(pool, seedFn);

    var paper = pool.slice(0, want);

    /* Course tracks draw on the subject bank first — an information systems
       midterm should ask about normalisation, not percentage change. */
    if (paper.length < want && exam.group === 'course' && window.XP_SUBJECTS) {
      var subjItems = window.XP_SUBJECTS.generate({
        subject: exam.subject || 'general',
        count: want - paper.length,
        seed: cfg.seed,
        difficulty: cfg.difficulty,
        sections: (exam.sections || []).filter(function (s) {
          return !cfg.sections || !cfg.sections.length || cfg.sections.indexOf(s.id) > -1;
        }).map(function (s) { return s.id; }),
        prefix: exam.id,
        examId: exam.id
      });
      paper = paper.concat(subjItems);
    }


    /* Section-aware filler.
       The previous approach generated a mixed bag and then re-stamped each
       item's section label to match the student's filter — so choosing
       "Science only" on the TEAS served vocabulary questions labelled
       Science. Content is now chosen BY section: each section is classified
       (reading / grammar / vocabulary / maths / science / clinical /
       concept) and only matching generators are used. */
    if (paper.length < want && window.XP_SECTIONS) {
      paper = paper.concat(window.XP_SECTIONS.generatePaper({
        exam: exam,
        count: want - paper.length,
        sections: cfg.sections,
        seed: cfg.seed,
        difficulty: cfg.difficulty
      }));
    }

    /* Last-resort top-up if a section produced nothing at all. */
    if (paper.length < want && GEN) {
      var gen = GEN.generate({
        count: want - paper.length,
        seed: cfg.seed,
        difficulty: cfg.difficulty,
        course: exam.group === 'course' ? exam : { id: exam.id, name: exam.name, topics: [], kind: exam.science ? 'clinical' : 'quant' },
        prefix: exam.id
      });
      var target = (exam.sections || []).filter(function (s) {
        return !cfg.sections || !cfg.sections.length || cfg.sections.indexOf(s.id) > -1;
      });
      if (target.length) gen.forEach(function (item, i) { item.section = target[i % target.length].id; });
      paper = paper.concat(gen);
    }

    /* Nothing may survive that is outside the chosen sections. This is the
       final guarantee behind the section filter, whatever produced the item. */
    if (cfg.sections && cfg.sections.length) {
      paper = paper.filter(function (it) { return cfg.sections.indexOf(it.section) > -1; });
    }

    /* Interleave so a sitting does not front-load all curated items */
    if (cfg.difficulty === 'combined') paper = shuffle(paper, seedFn);

    return paper.slice(0, want);
  }

  /* ---------------------------------------------------------------------
     Premium gating
     --------------------------------------------------------------------- */
  function unlockCap(examId) {
    var s = state();
    var u = s.unlocks[examId];
    if (u && u.granted) return Math.min(u.cap || LIM.premiumMax, LIM.premiumMax);
    return LIM.freeMax;
  }
  function redeemCode(examId, code) {
    code = (code || '').trim().toUpperCase();
    /* Codes are issued by the store after the $12 purchase is approved.
       Format: TMOC-<4 alnum>-<cap in hundreds>  e.g. TMOC-8H2K-20 = 2000 items.
       Validation here is a client-side convenience check only; the authoritative
       check happens when the order is approved in the Shopify admin. */
    var m = /^TMOC-[A-Z0-9]{4}-(\d{1,2})$/.exec(code);
    if (!m) return { ok: false, msg: 'That code is not in the expected format. Codes look like TMOC-8H2K-20.' };
    var cap = Math.min(parseInt(m[1], 10) * 100, LIM.premiumMax);
    if (cap < LIM.freeMax) return { ok: false, msg: 'That code does not raise your limit above the free allowance.' };
    store.patch(function (s) {
      s.unlocks = s.unlocks || {};
      s.unlocks[examId] = { code: code, granted: Date.now(), cap: cap };
    });
    return { ok: true, cap: cap, msg: 'Unlocked. You can now build sittings of up to ' + cap + ' questions for this course.' };
  }

  /* =====================================================================
     HUB
     ===================================================================== */
  var HUB_WIRED = false;

  function renderHub(root) {
    buildCourseTracks();
    var all = CAT.concat(window.XP_COURSES || []);
    var s = state();

    var groups = [
      { id: 'nursing', anchor: 'nursing', title: 'Nursing Exams', copy: 'NCLEX, Next Gen, TEAS, HESI and the admission tests \u2014 built to each published blueprint.' },
      { id: 'proctored', anchor: 'proctored', title: 'ATI Proctored Exams', copy: 'Every ATI specialty proctored exam, weighted the way the real paper is weighted.' },
      { id: 'science', anchor: 'science', title: 'Pre-Med & Science', copy: 'Anatomy & Physiology, microbiology, chemistry, biology, pathophysiology and the MCAT.' },
      { id: 'quiz', anchor: 'quizzes', title: 'Practice Quizzes', copy: 'Short focused drills \u2014 dosage calculation, pharmacology, lab values, EKG and electrolytes.' },
      { id: 'certification', anchor: 'certifications', title: 'Certifications', copy: 'CNA, medical assistant, phlebotomy, NREMT, ACLS, CCRN, CEN, FNP and nurse educator.' },
      { id: 'course', anchor: 'courses', title: 'Course Midterms & Finals', copy: 'Every course in the store gets a midterm and a final automatically. Up to ' + LIM.freeMax + ' questions per sitting free; ' + LIM.freeMax + '\u2013' + LIM.premiumMax + ' with a premium unlock.' }
    ];

    var html = '';

    /* progress summary */
    var done = s.history.length;
    var avg = done ? Math.round(s.history.reduce(function (a, h) { return a + h.pct; }, 0) / done) : 0;
    var missedCount = Object.keys(s.missed).length;

    html += '<div class="xp-hero">' +
      '<h1>Exam Practice Platform</h1>' +
      '<p>Pick an exam, set your own length and difficulty, and sit it under real conditions. Every question is graded with a worked explanation, a diagram where the science calls for one, and a reference you can follow up.</p>' +
      '<div class="xp-hero-stats">' +
      '<div><strong>' + all.length + '</strong><span>PRACTICE TRACKS</span></div>' +
      '<div><strong>' + done + '</strong><span>SITTINGS COMPLETED</span></div>' +
      '<div><strong>' + (done ? avg + '%' : '—') + '</strong><span>YOUR AVERAGE</span></div>' +
      '<div><strong>' + (s.streak.count || 0) + '</strong><span>DAY STREAK</span></div>' +
      '</div></div>';

    /* resume banner */
    if (s.resume && s.resume.examId) {
      var rex = findExam(s.resume.examId);
      html += '<div class="xp-section" style="padding-bottom:0"><div class="xp-section__head">' +
        '<div class="xp-premium-note" style="display:flex;align-items:center;gap:14px;flex-wrap:wrap">' +
        '<span style="flex:1">You have an unfinished sitting: <strong>' + esc(rex ? rex.name : s.resume.examId) + '</strong> — question ' + (s.resume.idx + 1) + ' of ' + s.resume.total + '.</span>' +
        '<button class="xp-btn xp-btn--gold" data-xp-resume>Resume</button>' +
        '<button class="xp-btn xp-btn--ghost" data-xp-discard>Discard</button>' +
        '</div></div></div>';
    }

    /* missed-question deck */
    if (missedCount > 0) {
      html += '<div class="xp-section" style="padding-bottom:0"><div class="xp-section__head">' +
        '<div class="xp-premium-note" style="background:#e8f6ee;border-color:#bfe3d0;color:#14532d;display:flex;align-items:center;gap:14px;flex-wrap:wrap">' +
        '<span style="flex:1"><strong>' + missedCount + ' question' + (missedCount === 1 ? '' : 's') + '</strong> you have got wrong are waiting in your review deck. Spaced repetition is the highest-yield thing you can do with twenty minutes.</span>' +
        '<button class="xp-btn xp-btn--primary" data-xp-review-deck>Drill missed questions</button>' +
        '</div></div></div>';
    }

    /* search + difficulty filter */
    html += '<div class="xp-section"><div class="xp-filter">' +
      '<input type="search" id="xp-search" placeholder="Search exams and courses…" aria-label="Search practice tracks">' +
      '<button class="xp-chip" data-xp-group="all" aria-pressed="true">All</button>' +
      '<button class="xp-chip" data-xp-group="nursing" aria-pressed="false">Nursing Exams</button>' +
      '<button class="xp-chip" data-xp-group="proctored" aria-pressed="false">ATI Proctored</button>' +
      '<button class="xp-chip" data-xp-group="science" aria-pressed="false">Pre-Med &amp; Science</button>' +
      '<button class="xp-chip" data-xp-group="quiz" aria-pressed="false">Practice Quizzes</button>' +
      '<button class="xp-chip" data-xp-group="certification" aria-pressed="false">Certifications</button>' +
      '<button class="xp-chip" data-xp-group="course" aria-pressed="false">Courses</button>' +
      '</div>';

    groups.forEach(function (g) {
      var list = all.filter(function (x) { return x.group === g.id; });
      if (!list.length) return;
      html += '<div class="xp-group" data-xp-groupsec="' + g.id + '" id="' + g.anchor + '">' +
        '<div class="xp-section__head" style="margin-top:34px"><h2>' + esc(g.title) + '</h2><p>' + esc(g.copy) + '</p></div>' +
        '<div class="xp-grid">';
      list.forEach(function (x) {
        var best = 0;
        s.history.forEach(function (h) { if (h.examId === x.id && h.pct > best) best = h.pct; });
        var curated = curatedFor(x.id).length;
        html += '<article class="xp-card" data-xp-name="' + esc((x.name + ' ' + (x.blurb || '')).toLowerCase()) + '">' +
          (best ? '<span class="xp-card__score">Best ' + best + '%</span>' : '') +
          '<span class="xp-card__icon" aria-hidden="true">' + (x.icon || '📄') + '</span>' +
          '<h3 class="xp-card__title">' + esc(x.name) + '</h3>' +
          '<p class="xp-card__desc">' + esc(x.blurb || '') + '</p>' +
          '<div class="xp-card__meta">' +
          '<span class="xp-tag xp-tag--free">Up to ' + LIM.freeMax + ' free</span>' +
          (curated ? '<span class="xp-tag">' + curated + ' written items</span>' : '') +
          (x.pass ? '<span class="xp-tag">Pass ' + x.pass + '%</span>' : '') +
          '</div>' +
          '<button class="xp-card__cta" data-xp-start="' + esc(x.id) + '">Start practising</button>' +
          '</article>';
      });
      html += '</div></div>';
    });
    html += '</div>';

    /* dashboard */
    html += renderDashboard(s);

    root.innerHTML = html;
    wireHub(root);
  }

  function renderDashboard(s) {
    var h = '<div class="xp-section xp-section--alt" id="dashboard"><div class="xp-section__head">' +
      '<h2>Your Progress Dashboard</h2><p>Everything below is stored in this browser only. Nothing is uploaded.</p></div>' +
      '<div style="max-width:var(--max-width);margin:0 auto">';

    if (!s.history.length) {
      h += '<div class="xp-empty"><p style="font-size:34px;margin-bottom:8px">📊</p>' +
        '<p>No sittings yet. Finish one and your score history, weak topics and pass-rate trend appear here.</p></div>';
    } else {
      var recent = s.history.slice(-12).reverse();
      var byExam = {};
      s.history.forEach(function (r) {
        byExam[r.examId] = byExam[r.examId] || { n: 0, sum: 0, best: 0, name: r.examName };
        byExam[r.examId].n++; byExam[r.examId].sum += r.pct;
        if (r.pct > byExam[r.examId].best) byExam[r.examId].best = r.pct;
      });

      h += '<div class="xp-stat-grid">' +
        '<div class="xp-stat"><b>' + s.history.length + '</b><span>Sittings</span></div>' +
        '<div class="xp-stat"><b>' + Math.round(s.history.reduce(function (a, r) { return a + r.pct; }, 0) / s.history.length) + '%</b><span>Average score</span></div>' +
        '<div class="xp-stat"><b>' + s.history.reduce(function (a, r) { return a + (r.attempted != null ? r.attempted : r.total); }, 0) + '</b><span>Questions attempted</span></div>' +
        '<div class="xp-stat"><b>' + s.history.reduce(function (a, r) { return a + r.total; }, 0) + '</b><span>Questions seen</span></div>' +
        '<div class="xp-stat"><b>' + s.history.reduce(function (a, r) { return a + r.correct; }, 0) + '</b><span>Correct all-time</span></div>' +
        '<div class="xp-stat"><b>' + (s.streak.count || 0) + '</b><span>Day streak</span></div>' +
        '</div>';

      h += '<div class="xp-breakdown"><h3>Performance by track</h3>';
      Object.keys(byExam).forEach(function (k) {
        var b = byExam[k], avg = Math.round(b.sum / b.n);
        h += '<div class="xp-meter"><div class="xp-meter__top"><span>' + esc(b.name) + ' <small style="color:var(--xp-muted)">(' + b.n + ' sitting' + (b.n === 1 ? '' : 's') + ')</small></span><b>' + avg + '% avg · ' + b.best + '% best</b></div>' +
          '<div class="xp-meter__track"><div class="xp-meter__fill" style="width:' + avg + '%"></div></div></div>';
      });
      h += '</div>';

      /* Cumulative topic mastery per track */
      var mst = s.mastery || {};
      Object.keys(mst).forEach(function (exId) {
        var map = mst[exId];
        if (!map || !Object.keys(map).length) return;
        var ex = findExam(exId);
        var nm = (ex && ex.name) || (byExam[exId] && byExam[exId].name) || exId;
        h += masteryHTML(map, 'Topic mastery \u2014 ' + nm,
          'Every question you have answered on this track, combined.');
      });

      h += '<div class="xp-breakdown"><h3>Recent sittings</h3>';
      recent.forEach(function (r) {
        h += '<div style="display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--xp-line);font-size:14px;gap:12px;flex-wrap:wrap">' +
          '<span>' + esc(r.examName) + ' <small style="color:var(--xp-muted)">' + new Date(r.at).toLocaleDateString() + ' · ' + esc(r.difficulty) + ' · ' + esc(r.mode) + '</small></span>' +
          '<strong style="color:' + (r.pct >= (r.pass || 70) ? 'var(--xp-correct)' : 'var(--xp-wrong)') + '">' + r.correct + '/' + r.total + ' — ' + r.pct + '%</strong></div>';
      });
      h += '</div>';

      h += '<div style="text-align:center;margin-top:18px"><button class="xp-btn xp-btn--ghost" data-xp-clear>Clear all saved progress</button></div>';
    }

    h += '</div></div>';
    return h;
  }

  function wireHub(root) {
    /* search */
    var search = $('#xp-search', root);
    if (search) {
      search.addEventListener('input', function () {
        var q = this.value.toLowerCase().trim();
        $$('.xp-card', root).forEach(function (c) {
          c.style.display = (!q || c.getAttribute('data-xp-name').indexOf(q) > -1) ? '' : 'none';
        });
      });
    }
    /* group chips */
    $$('[data-xp-group]', root).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var g = this.getAttribute('data-xp-group');
        $$('[data-xp-group]', root).forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        $$('[data-xp-groupsec]', root).forEach(function (sec) {
          sec.style.display = (g === 'all' || sec.getAttribute('data-xp-groupsec') === g) ? '' : 'none';
        });
      });
    });
    /* start buttons — attached once per hub element, never stacked */
    if (HUB_WIRED) return;
    HUB_WIRED = true;
    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-xp-start]');
      if (b) { openSetup(b.getAttribute('data-xp-start')); return; }
      if (e.target.closest('[data-xp-resume]')) { resumeSession(); return; }
      if (e.target.closest('[data-xp-discard]')) {
        store.patch(function (s) { s.resume = null; });
        location.reload(); return;
      }
      if (e.target.closest('[data-xp-review-deck]')) { startMissedDeck(); return; }
      if (e.target.closest('[data-xp-clear]')) {
        if (confirm('This deletes your score history, streak and review deck from this browser. Continue?')) {
          try { localStorage.removeItem(KEY); } catch (err) {}
          location.reload();
        }
      }
    });

    /* deep link: ?exam=ati-teas */
    var deep = param('exam');
    if (deep && findExam(deep)) setTimeout(function () { openSetup(deep); }, 220);
  }

  /* =====================================================================
     SETUP MODAL
     ===================================================================== */
  function openSetup(examId) {
    var exam = findExam(examId);
    if (!exam) { toast('That practice track could not be found.'); return; }

    var cap = unlockCap(examId);
    var curated = curatedFor(examId).length;
    var defaultCount = Math.min(exam.fullLen || 30, 25);

    /* Rebuild the node each time. Re-using it would stack a fresh click
       handler on every open, and "Start exam" would fire once per stack. */
    var old = $('#xp-modal');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var m = el('div', 'xp-modal');
    m.id = 'xp-modal';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    document.body.appendChild(m);

    /* Checkable section list.
       The old version was a row of pills whose only feedback was a colour
       change, so "all selected" and "nothing deliberately selected" looked
       and behaved the same. Each row now carries an explicit tick box, the
       header shows a live count, and Select all / Clear all make the state
       unambiguous. */
    var secList = (exam.sections || []);
    var sectionOpts = secList.map(function (sec) {
      var w = sec.weight ? '<em class="xp-check__w">' + sec.weight + '% of the real exam</em>' : '';
      return '<label class="xp-check">' +
        '<input type="checkbox" name="xp-sec" value="' + esc(sec.id) + '" checked>' +
        '<span class="xp-check__box" aria-hidden="true"></span>' +
        '<span class="xp-check__body"><b>' + esc(sec.name) + '</b>' + w + '</span>' +
      '</label>';
    }).join('');

    var sectionHead =
      '<div class="xp-check-head">' +
        '<span class="xp-check-count" id="xp-sec-count">' +
          secList.length + ' of ' + secList.length + ' sections selected' +
        '</span>' +
        '<span class="xp-check-actions">' +
          '<button type="button" class="xp-linkbtn" data-xp-sec-all>Select all</button>' +
          '<button type="button" class="xp-linkbtn" data-xp-sec-none>Clear all</button>' +
        '</span>' +
      '</div>';
    sectionOpts = sectionHead + '<div class="xp-check-list">' + sectionOpts + '</div>';

    var diffOpts = DIFFS.map(function (d, i) {
      return '<label class="xp-opt"><input type="radio" name="xp-diff" value="' + d.id + '"' + (d.id === 'combined' ? ' checked' : '') + '><span>' + esc(d.name) + '</span></label>';
    }).join('');

    m.innerHTML =
      '<div class="xp-modal__panel">' +
        '<div class="xp-modal__head">' +
          '<button class="xp-modal__close" data-xp-close aria-label="Close">&times;</button>' +
          '<h3>' + (exam.icon || '📄') + ' ' + esc(exam.name) + '</h3>' +
          '<p>' + esc(exam.blurb || '') + '</p>' +
        '</div>' +
        '<div class="xp-modal__body">' +

          '<div class="xp-field">' +
            '<label for="xp-count">How many questions?</label>' +
            '<div class="xp-count-row">' +
              '<input type="range" id="xp-count-range" min="1" max="' + cap + '" value="' + defaultCount + '" step="1">' +
              '<input type="number" id="xp-count" min="1" max="' + LIM.premiumMax + '" value="' + defaultCount + '">' +
            '</div>' +
            '<p class="xp-hint">Anything from a single question to <strong>' + cap + '</strong>. ' +
              (curated ? curated + ' items are hand-written for this track; beyond that the platform builds fresh items from the same blueprint, each with a full worked explanation.' : 'Items are built from this track\u2019s published blueprint, each with a full worked explanation.') +
            '</p>' +
            (cap <= LIM.freeMax
              ? '<div class="xp-premium-note" id="xp-premium-note" hidden>' +
                  '<strong>Beyond ' + LIM.freeMax + ' questions</strong> a sitting needs a premium unlock — $' + LIM.premiumPrice + ' USD, one-off, per course, valid up to ' + LIM.premiumMax + ' questions. ' +
                  'Unlocks are released after admin approval. <a href="/products/' + esc(LIM.premiumHandle || 'exam-practice-premium-unlock') + '">Buy an unlock</a> or ' +
                  '<button type="button" class="xp-btn xp-btn--ghost xp-btn--sm" data-xp-redeem style="padding:4px 12px;font-size:12px;margin-left:4px">enter a code</button>' +
                '</div>'
              : '<div class="xp-premium-note" style="background:#e8f6ee;border-color:#bfe3d0;color:#14532d">Premium unlocked for this course — up to ' + cap + ' questions.</div>') +
          '</div>' +

          '<div class="xp-field">' +
            '<span class="xp-field__legend">Difficulty</span>' +
            '<div class="xp-options">' + diffOpts + '</div>' +
            '<p class="xp-hint" id="xp-diff-hint">' + esc((DIFFS[DIFFS.length - 1] || {}).hint || '') + '</p>' +
          '</div>' +

          '<div class="xp-field">' +
            '<span class="xp-field__legend">Mode</span>' +
            '<div class="xp-options">' +
              '<label class="xp-opt"><input type="radio" name="xp-mode" value="tutor" checked><span>Tutor — feedback as you go</span></label>' +
              '<label class="xp-opt"><input type="radio" name="xp-mode" value="exam"><span>Real exam — no feedback until the end</span></label>' +
            '</div>' +
            '<p class="xp-hint">Tutor mode lets you check each answer before moving on. Real exam mode mirrors the live environment: timer running, answers hidden, one shot.</p>' +
          '</div>' +

          '<div class="xp-field">' +
            '<span class="xp-field__legend">Timing</span>' +
            '<div class="xp-options">' +
              '<label class="xp-opt"><input type="radio" name="xp-timing" value="off" checked><span>Untimed</span></label>' +
              '<label class="xp-opt"><input type="radio" name="xp-timing" value="paced"><span>Paced (real exam seconds per question)</span></label>' +
              '<label class="xp-opt"><input type="radio" name="xp-timing" value="full"><span>Full length (' + (exam.minutes || 60) + ' min)</span></label>' +
            '</div>' +
          '</div>' +

          (sectionOpts ? '<div class="xp-field"><span class="xp-field__legend">Sections to include</span><div class="xp-options">' + sectionOpts + '</div></div>' : '') +

        '</div>' +
        '<div class="xp-modal__foot">' +
          '<button class="xp-btn xp-btn--ghost" data-xp-close>Cancel</button>' +
          '<button class="xp-btn xp-btn--gold" data-xp-begin>Start exam &rarr;</button>' +
        '</div>' +
      '</div>';

    m.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    var range = $('#xp-count-range', m), num = $('#xp-count', m), note = $('#xp-premium-note', m);
    function sync(v) {
      v = Math.max(1, Math.min(LIM.premiumMax, parseInt(v, 10) || 1));
      num.value = v;
      range.value = Math.min(v, cap);
      if (note) note.hidden = v <= LIM.freeMax;
    }
    range.addEventListener('input', function () { sync(this.value); });
    num.addEventListener('input', function () { sync(this.value); });

    $$('[name="xp-diff"]', m).forEach(function (r) {
      r.addEventListener('change', function () {
        var d = DIFFS.filter(function (x) { return x.id === r.value; })[0];
        var hint = $('#xp-diff-hint', m);
        if (d && hint) hint.textContent = d.hint;
      });
    });

    function readSecs() {
      var out = [];
      /* Attribute-only selector, deliberately. A compound `input[name=...]`
         is fine in browsers but fails in stricter/older query engines, and a
         silently empty result here reads to the user as "nothing selected". */
      var boxes = m.querySelectorAll('[name="xp-sec"]');
      for (var i = 0; i < boxes.length; i++) {
        if (boxes[i] && boxes[i].checked) out.push(boxes[i].value);
      }
      return out;
    }

    function paintSecCount() {
      var lbl = m.querySelector('#xp-sec-count');
      if (!lbl) return;
      var n = readSecs().length, total = secList.length;
      lbl.textContent = n + ' of ' + total + ' section' + (total === 1 ? '' : 's') + ' selected';
      lbl.classList.toggle('is-empty', n === 0);
      lbl.classList.toggle('is-all', n === total && total > 0);
    }

    function setAllSecs(on) {
      var boxes = m.querySelectorAll('[name="xp-sec"]');
      for (var i = 0; i < boxes.length; i++) boxes[i].checked = !!on;
      paintSecCount();
    }

    m.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'xp-sec') paintSecCount();
    });

    m.addEventListener('click', function (e) {
      if (e.target && e.target.closest) {
        if (e.target.closest('[data-xp-sec-all]')) { e.preventDefault(); setAllSecs(true); return; }
        if (e.target.closest('[data-xp-sec-none]')) { e.preventDefault(); setAllSecs(false); return; }
      }
      if (e.target === m || e.target.closest('[data-xp-close]')) closeSetup();
      if (e.target.closest('[data-xp-redeem]')) {
        var code = prompt('Enter your premium unlock code (issued after approval):');
        if (code) {
          var res = redeemCode(examId, code);
          toast(res.msg, 5000);
          if (res.ok) { closeSetup(); openSetup(examId); }
        }
      }
      if (e.target.closest('[data-xp-begin]')) {
        var count = parseInt(num.value, 10) || 1;
        if (count > unlockCap(examId)) {
          toast('That exceeds your current limit of ' + unlockCap(examId) + ' questions. A premium unlock raises it to ' + LIM.premiumMax + '.', 5200);
          return;
        }
        var secs = readSecs();
        if (secList.length && !secs.length) {
          toast('Choose at least one section to practise.', 4000);
          var firstBox = m.querySelector('.xp-check');
          if (firstBox) { firstBox.classList.add('is-nudge'); setTimeout(function () { firstBox.classList.remove('is-nudge'); }, 900); }
          return;
        }
        beginSession({
          examId: examId,
          count: count,
          difficulty: ($$('[name="xp-diff"]:checked', m)[0] || {}).value || 'combined',
          mode: ($$('[name="xp-mode"]:checked', m)[0] || {}).value || 'tutor',
          timing: ($$('[name="xp-timing"]:checked', m)[0] || {}).value || 'off',
          sections: secs
        });
      }
    });

    document.addEventListener('keydown', escClose);
  }
  function escClose(e) { if (e.key === 'Escape') closeSetup(); }
  function closeSetup() {
    var m = $('#xp-modal');
    if (m) m.classList.remove('is-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', escClose);
  }

  /* =====================================================================
     SESSION
     ===================================================================== */
  var SESSION = null;
  var TICK = null;

  function beginSession(cfg) {
    var exam = findExam(cfg.examId);
    if (!exam) {
      closeSetup();
      showFatal('That practice track could not be found.', 'examId=' + cfg.examId);
      return;
    }

    var seed = Math.floor(Math.random() * 100000);
    var paper;
    try {
      paper = assemblePaper({
        exam: exam, count: cfg.count, difficulty: cfg.difficulty,
        sections: cfg.sections, seed: seed
      });
    } catch (err) {
      closeSetup();
      showFatal('The question paper could not be assembled.', err && err.message);
      return;
    }

    if (!paper || !paper.length) {
      /* Retry once with the filters dropped before giving up — an over-narrow
         section-plus-difficulty combination is the usual cause, and silently
         doing nothing is the behaviour that reads as "it is broken". */
      try {
        paper = assemblePaper({ exam: exam, count: cfg.count, difficulty: 'combined', sections: null, seed: seed });
      } catch (e2) { paper = []; }

      if (!paper || !paper.length) {
        closeSetup();
        showFatal('No questions could be built for this track.',
          'exam=' + exam.id + ' requested=' + cfg.count);
        return;
      }
      toast('Those filters were too narrow, so this paper uses all sections and difficulties.', 5000);
    }

    var limitSec = 0;
    if (cfg.timing === 'full') limitSec = (exam.minutes || 60) * 60;
    if (cfg.timing === 'paced') {
      var perQ = ((exam.minutes || 60) * 60) / (exam.fullLen || 50);
      limitSec = Math.round(perQ * paper.length);
    }

    SESSION = {
      examId: cfg.examId, examName: exam.name, pass: exam.pass || 70,
      mode: cfg.mode, difficulty: cfg.difficulty, timing: cfg.timing,
      sections: cfg.sections || null,
      seed: seed, total: paper.length, idx: 0,
      paper: paper,
      answers: new Array(paper.length).fill(null),
      revealed: new Array(paper.length).fill(false),
      flags: new Array(paper.length).fill(false),
      times: new Array(paper.length).fill(0),
      startedAt: Date.now(), limitSec: limitSec, elapsed: 0, qStart: Date.now()
    };

    closeSetup();
    mountRunner();
    startTick();
    persistResume();
  }

  function persistResume() {
    if (!SESSION) return;
    store.patch(function (s) {
      s.resume = {
        examId: SESSION.examId, idx: SESSION.idx, total: SESSION.total,
        answers: SESSION.answers, revealed: SESSION.revealed, flags: SESSION.flags,
        mode: SESSION.mode, difficulty: SESSION.difficulty, timing: SESSION.timing,
        seed: SESSION.seed, elapsed: SESSION.elapsed, limitSec: SESSION.limitSec,
        sections: SESSION.sections || null,
        paperIds: SESSION.paper.map(function (p) { return p.id; })
      };
    });
  }

  function resumeSession() {
    var s = state();
    var r = s.resume;
    if (!r) return;
    var exam = findExam(r.examId);
    if (!exam) { toast('That exam is no longer available.'); return; }
    var paper = assemblePaper({ exam: exam, count: r.total, difficulty: r.difficulty, sections: r.sections || null, seed: r.seed });
    SESSION = {
      examId: r.examId, examName: exam.name, pass: exam.pass || 70,
      mode: r.mode, difficulty: r.difficulty, timing: r.timing, seed: r.seed,
      total: paper.length, idx: Math.min(r.idx, paper.length - 1), paper: paper,
      answers: r.answers || new Array(paper.length).fill(null),
      revealed: r.revealed || new Array(paper.length).fill(false),
      flags: r.flags || new Array(paper.length).fill(false),
      times: new Array(paper.length).fill(0),
      startedAt: Date.now(), limitSec: r.limitSec || 0, elapsed: r.elapsed || 0, qStart: Date.now()
    };
    mountRunner();
    startTick();
    toast('Resumed where you left off.');
  }

  function startMissedDeck() {
    var s = state();
    var ids = Object.keys(s.missed);
    var paper = BANK.filter(function (it) { return ids.indexOf(it.id) > -1; });
    if (!paper.length) { toast('Your review deck only holds generated items, which cannot be replayed. Sit a fresh paper instead.', 4600); return; }
    SESSION = {
      examId: 'review-deck', examName: 'Missed Questions Review', pass: 100,
      mode: 'tutor', difficulty: 'combined', timing: 'off', seed: 1,
      total: paper.length, idx: 0, paper: shuffle(paper),
      answers: new Array(paper.length).fill(null),
      revealed: new Array(paper.length).fill(false),
      flags: new Array(paper.length).fill(false),
      times: new Array(paper.length).fill(0),
      startedAt: Date.now(), limitSec: 0, elapsed: 0, qStart: Date.now()
    };
    mountRunner();
    startTick();
  }

  function startTick() {
    clearInterval(TICK);
    TICK = setInterval(function () {
      if (!SESSION) return;
      SESSION.elapsed++;
      var t = $('#xp-timer');
      if (t) {
        if (SESSION.limitSec) {
          var left = SESSION.limitSec - SESSION.elapsed;
          t.textContent = fmtTime(left);
          t.classList.toggle('is-low', left <= 60);
          if (left <= 0) { finish(true); return; }
        } else {
          t.textContent = fmtTime(SESSION.elapsed);
        }
      }
      if (SESSION.elapsed % 10 === 0) persistResume();
    }, 1000);
  }

  /* ---------------------------------------------------------------------
     Runner UI
     --------------------------------------------------------------------- */
  /* The runner has to end up somewhere the user can actually see. Mounting
     next to the hub is right in principle, but it inherits whatever the
     surrounding theme does — a hidden ancestor, a zero-height wrapper, an
     overflow clip. So mount, then VERIFY, and relocate if the element did
     not actually render. A blank page with no error is the worst outcome;
     this makes that state impossible. */
  function isVisible(node) {
    if (!node || !node.getBoundingClientRect) return false;
    /* offsetParent is null when the element or any ancestor is display:none
       (except for position:fixed, which we never use on the mount node). */
    if (node.offsetParent === null) return false;
    var r = node.getBoundingClientRect();
    return r.width > 0;
  }

  function forceVisible(node) {
    node.style.display = 'block';
    node.style.visibility = 'visible';
    node.style.opacity = '1';
    node.style.position = 'static';
    node.style.height = 'auto';
    node.style.maxHeight = 'none';
    node.style.overflow = 'visible';
  }

  /* THE MOUNT.
     Earlier builds hid the hub and created a sibling element to hold the exam.
     That sibling inherited whatever the surrounding theme did to it, and on
     some pages it never became visible — you got a scroll to the top and a
     blank screen, with no error, because nothing had actually thrown.

     The hub itself is demonstrably visible: you can see the cards and click
     them. So the exam now renders INSIDE the hub, reusing the exact element
     that was already displaying correctly. There is no second container to
     go missing. */
  function runnerRoot() {
    var r = $('#xp-app');
    if (r) return r;

    var hub = $('#xp-hub');
    if (hub) {
      hub.style.display = '';
      hub.innerHTML = '';
      r = el('div');
      r.id = 'xp-app';
      hub.appendChild(r);
    } else {
      r = el('div');
      r.id = 'xp-app';
      (document.querySelector('main') || document.body).appendChild(r);
    }
    forceVisible(r);
    return r;
  }

  /* Called after the runner has content. If it did not render, walk up and
     clear whatever is hiding it; failing that, relocate to <main> or body. */
  function ensureRunnerVisible() {
    var r = $('#xp-app');
    if (!r) return false;
    if (isVisible(r)) return true;

    /* Step 1 — an ancestor is hidden or collapsed. Unhide the chain. */
    var node = r.parentNode, guard = 0;
    while (node && node !== document.body && guard++ < 12) {
      var cs = window.getComputedStyle ? window.getComputedStyle(node) : null;
      if (cs) {
        if (cs.display === 'none') node.style.display = 'block';
        if (cs.visibility === 'hidden') node.style.visibility = 'visible';
        if (parseFloat(cs.opacity) === 0) node.style.opacity = '1';
        if (cs.maxHeight && cs.maxHeight !== 'none' && parseFloat(cs.maxHeight) === 0) node.style.maxHeight = 'none';
      }
      node = node.parentNode;
    }
    forceVisible(r);
    if (isVisible(r)) return true;

    /* Step 2 — relocate wholesale. */
    var host = document.querySelector('main') || document.querySelector('#MainContent') || document.body;
    if (r.parentNode !== host) {
      host.appendChild(r);
      forceVisible(r);
    }
    return isVisible(r);
  }

  function showFatal(msg, detail) {
    var r = $('#xp-app') || document.body;
    var box = el('div', 'xp-fatal');
    box.innerHTML =
      '<h3>The practice session could not start</h3>' +
      '<p>' + esc(msg) + '</p>' +
      (detail ? '<pre>' + esc(String(detail)) + '</pre>' : '') +
      '<p style="margin-top:14px"><button class="xp-btn xp-btn--ghost" data-xp-reload>Reload the page</button> ' +
      '<a class="xp-btn xp-btn--primary" href="/pages/contact">Tell us what happened</a></p>';
    if (r.firstChild) r.insertBefore(box, r.firstChild); else r.appendChild(box);
    box.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[data-xp-reload]')) window.location.reload();
    });
    ensureRunnerVisible();
    try { box.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
  }

  /* Bring the hub back when a sitting ends or is abandoned. */
  function showHubAgain() {
    var hub = $('#xp-hub');
    if (!hub) return;
    hub.style.display = '';
    hub.innerHTML = '';
    /* HUB_WIRED stays true on purpose: the delegated click listener lives on
       the hub element itself and survives clearing its children. Re-arming it
       here would double-fire every card. */
    renderHub(hub);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { window.scrollTo(0, 0); }
  }

  /* Related services shown inside the runner, so a student practising TEAS
     always sees the HESI, NCLEX and tutoring options alongside it. */
  var XP_RELATED = {
    'ati-teas':   ['hesi','nclex-rn','nclex-pn','knat','nln-nex','dosage-calculation','ati-anatomy'],
    'hesi':       ['ati-teas','nclex-rn','nclex-pn','ati-anatomy','ati-pharmacology','ati-med-surg-rn','dosage-calculation'],
    'nclex-rn':   ['nclex-pn','ati-pharmacology','ati-med-surg-rn','ati-maternal','ati-pediatric','ati-mental-health','dosage-calculation','hesi','ati-teas'],
    'nclex-pn':   ['nclex-rn','ati-med-surg-pn','ati-pharmacology','dosage-calculation','hesi','ati-teas'],
    'ati-med-surg-rn': ['ati-med-surg-pn','nclex-rn','ati-pharmacology','ati-anatomy','hesi'],
    'ati-med-surg-pn': ['ati-med-surg-rn','nclex-pn','ati-pharmacology','dosage-calculation'],
    'dosage-calculation': ['nclex-rn','nclex-pn','hesi','ati-pharmacology','ati-teas'],
    'nremt':      ['nclex-rn','medical-assistant','phlebotomy','dosage-calculation'],
    'knat':       ['ati-teas','hesi','nln-nex','nclex-rn'],
    'nln-nex':    ['ati-teas','hesi','knat','nclex-rn'],
    'fnp':        ['nclex-rn','ati-pharmacology','ati-med-surg-rn'],
    'ged':        ['tsi','clep','asvab','math-placement'],
    'gre':        ['gmat','lsat','mcat','toefl'],
    'gmat':       ['gre','lsat','toefl'],
    'lsat':       ['gre','gmat'],
    'mcat':       ['gre','ati-anatomy'],
    'pmp':        ['comptia','wgu','ccat'],
    'comptia':    ['pmp','wgu','ccat'],
    'real-estate':['pmp','comptia'],
    'aleks':      ['math-placement','tsi','ged'],
    'asvab':      ['ged','wonderlic','ccat'],
    'medical-assistant': ['phlebotomy','nremt','ati-teas'],
    'phlebotomy': ['medical-assistant','nremt','ati-teas']
  };

  function relatedTagsHTML(examId) {
    var ex = findExam(examId);
    var ids = XP_RELATED[examId];

    /* Course tracks: point at the product plus its sibling paper. */
    if (!ids && ex && ex.group === 'course') {
      var other = ex.form === 'midterm' ? 'final' : 'midterm';
      return '<div class="xptags"><h3 class="xptags__title">Also for this course</h3><div class="xptags__row">' +
        '<a class="xptags__tag" href="/pages/exam-practice?exam=course-' + esc(ex.productHandle) + '-' + other + '">' +
          'Practise the ' + (other === 'final' ? 'Final' : 'Midterm') + '</a>' +
        '<a class="xptags__tag" href="' + esc(ex.productUrl || ('/products/' + ex.productHandle)) + '">View this course</a>' +
        '<a class="xptags__tag" href="/pages/order-now?service=onlineclass">Take this class for me</a>' +
        '<a class="xptags__tag" href="/pages/order-now?service=writing">Assignment help</a>' +
        '</div></div>';
    }
    if (!ids) ids = ['ati-teas', 'nclex-rn', 'ged', 'pmp'];

    var links = ids.map(function (id) {
      var e = findExam(id);
      return e ? '<a class="xptags__tag" href="/pages/exam-practice?exam=' + esc(id) + '">' + esc(e.name) + '</a>' : '';
    }).filter(Boolean).join('');

    return '<div class="xptags"><h3 class="xptags__title">Related exams &amp; services</h3><div class="xptags__row">' +
      links +
      '<a class="xptags__tag" href="/pages/order-now?service=onlineclass">Take my class</a>' +
      '<a class="xptags__tag" href="/pages/order-now?service=writing">Assignment help</a>' +
      '</div></div>';
  }

  function mountRunner() {
    var root = runnerRoot();
    root.innerHTML =
      '<div class="xp-runner">' +
        '<div class="xp-bar">' +
          '<span class="xp-bar__title">' + esc(SESSION.examName) + '</span>' +
          '<span class="xp-bar__stat"><small>Question</small> <b id="xp-pos">1 / ' + SESSION.total + '</b></span>' +
          '<span class="xp-bar__stat"><small>Answered</small> <b id="xp-answered">0</b></span>' +
          '<span class="xp-bar__stat"><small>Flagged</small> <b id="xp-flagged">0</b></span>' +
          '<span class="xp-bar__stat"><small>' + (SESSION.limitSec ? 'Remaining' : 'Elapsed') + '</small> <b class="xp-timer" id="xp-timer">' + fmtTime(SESSION.limitSec || 0) + '</b></span>' +
          '<div class="xp-progress"><div class="xp-progress__fill" id="xp-progressfill"></div></div>' +
        '</div>' +
        '<div class="xp-layout">' +
          '<div id="xp-qwrap"></div>' +
          '<aside class="xp-side">' +
            '<div class="xp-panel"><h4>Question navigator</h4><div class="xp-navgrid" id="xp-nav"></div>' +
              '<div class="xp-legend">' +
                '<span><i style="background:var(--color-primary)"></i>Answered</span>' +
                '<span><i style="border-color:var(--color-gold);border-width:2px"></i>Flagged</span>' +
                '<span><i></i>Untouched</span>' +
              '</div></div>' +
            '<div class="xp-panel"><h4>This sitting</h4>' +
              '<div class="xp-meter"><div class="xp-meter__top"><span>Progress</span><b id="xp-pctdone">0%</b></div>' +
              '<div class="xp-meter__track"><div class="xp-meter__fill" id="xp-sidefill"></div></div></div>' +
              '<p style="font-size:12px;color:var(--xp-muted);line-height:1.6">' +
                'Mode: <strong>' + (SESSION.mode === 'exam' ? 'Real exam' : 'Tutor') + '</strong><br>' +
                'Difficulty: <strong style="text-transform:capitalize">' + esc(SESSION.difficulty) + '</strong><br>' +
                'You can end the sitting at any point — answered or not, you get every explanation.' +
              '</p>' +
              '<button class="xp-btn xp-btn--danger" data-xp-end style="width:100%;margin-top:10px">End exam &amp; see answers</button>' +
              '<button class="xp-btn xp-btn--ghost" data-xp-hub style="width:100%;margin-top:8px">Leave without grading</button>' +
            '</div>' +
            relatedTagsHTML(SESSION.examId) +
            '<div style="display:none">' +
            '</div>' +
          '</aside>' +
        '</div>' +
      '</div>';

    root.addEventListener('click', runnerClick);
    document.addEventListener('keydown', runnerKeys);

    try {
      drawQuestion();
    } catch (err) {
      showFatal('Something went wrong building the first question.', err && err.message);
      return;
    }

    /* Verify. If the question did not make it into the DOM, or the runner is
       not visible, fix it here rather than leaving a blank page. */
    var wrap = $('#xp-qwrap');
    if (!wrap || !wrap.innerHTML) {
      showFatal('The questions were prepared but did not render.', 'paper=' + (SESSION && SESSION.paper.length));
      return;
    }
    if (!ensureRunnerVisible()) {
      showFatal('The exam is loaded but the page is preventing it from showing.',
        'Try a different browser, or disable browser extensions for this page.');
      return;
    }

    /* Scroll to the runner, not to the top — on a long page the top is the
       hidden hub, which reads as "nothing happened". */
    try {
      var top = root.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } catch (e) { window.scrollTo(0, 0); }
  }

  function drawQuestion() {
    var i = SESSION.idx, q = SESSION.paper[i];
    var wrap = $('#xp-qwrap');
    var revealed = SESSION.revealed[i] || SESSION.mode === 'review';
    var given = SESSION.answers[i];

    var h = '<div class="xp-q">' +
      '<div class="xp-q__head">' +
        '<span class="xp-q__num">Question ' + (i + 1) + ' of ' + SESSION.total + '</span>' +
        '<span class="xp-diff xp-diff--' + esc(q.difficulty || 'moderate') + '">' + esc(q.difficulty || 'moderate') + '</span>' +
        (q.section ? '<span class="xp-tag">' + esc(sectionName(q)) + '</span>' : '') +
        (q.generated ? '<span class="xp-tag" title="Built from this exam\u2019s blueprint">auto-built</span>' : '') +
        '<button class="xp-flagbtn" data-xp-flag aria-pressed="' + (SESSION.flags[i] ? 'true' : 'false') + '">⚑ ' + (SESSION.flags[i] ? 'Flagged' : 'Flag') + '</button>' +
      '</div>';

    if (q.stimulus) h += '<div class="xp-stimulus">' + q.stimulus + '</div>';
    h += '<div class="xp-q__text">' + q.q + '</div>';

    if (q.type === 'entry') {
      h += '<div class="xp-entry">' +
        '<input type="text" id="xp-entryinput" inputmode="decimal" placeholder="Your answer" value="' + (given === null ? '' : esc(given)) + '"' + (revealed ? ' disabled' : '') + ' aria-label="Your answer">' +
        '<span class="xp-unit">Numeric answer</span></div>';
    } else {
      h += '<div class="xp-answers">';
      (q.opts || []).forEach(function (opt, k) {
        var cls = 'xp-answer';
        var mark = '';
        if (revealed) {
          cls += ' is-locked';
          if (k === q.a) { cls += ' is-correct'; mark = '<span class="xp-answer__mark">✓ Correct</span>'; }
          else if (k === given) { cls += ' is-wrong'; mark = '<span class="xp-answer__mark">✗ Your answer</span>'; }
        } else if (k === given) { cls += ' is-selected'; }
        h += '<button class="' + cls + '" data-xp-opt="' + k + '"' + (revealed ? ' disabled' : '') + '>' +
          '<span class="xp-answer__key">' + 'ABCDEFGH'.charAt(k) + '</span>' +
          '<span>' + opt + '</span>' + mark + '</button>';
      });
      h += '</div>';
    }

    if (revealed) h += explanationHTML(q, given);

    /* controls */
    h += '<div class="xp-controls">' +
      '<button class="xp-btn xp-btn--ghost" data-xp-prev' + (i === 0 ? ' disabled' : '') + '>&larr; Previous</button>' +
      (SESSION.mode === 'tutor' && !revealed
        ? '<button class="xp-btn xp-btn--primary" data-xp-check>Check this answer</button>'
        : '') +
      '<button class="xp-btn xp-btn--gold" data-xp-next>' + (i === SESSION.total - 1 ? 'Go to submit' : 'Next question &rarr;') + '</button>' +
      '<button class="xp-btn xp-btn--danger xp-btn--end" data-xp-submit>Submit entire exam</button>' +
      '</div>' +
      '<p style="font-size:11.5px;color:var(--xp-muted);margin-top:12px">' +
        'Keyboard: <strong>1–8</strong> choose · <strong>Enter</strong> check · <strong>→ ←</strong> move · <strong>F</strong> flag' +
      '</p>' +
      '</div>';

    wrap.innerHTML = h;

    var input = $('#xp-entryinput');
    if (input) {
      input.addEventListener('input', function () {
        SESSION.answers[i] = this.value.trim() === '' ? null : this.value.trim();
        refreshMeta();
      });
      if (!revealed) input.focus();
    }

    refreshMeta();
  }

  function sectionName(q) {
    var exam = findExam(q.exam) || findExam(SESSION.examId);
    if (exam && exam.sections) {
      for (var i = 0; i < exam.sections.length; i++) if (exam.sections[i].id === q.section) return exam.sections[i].name;
    }
    return String(q.section || '').replace(/-/g, ' ');
  }

  function explanationHTML(q, given) {
    var ok = isCorrect(q, given);
    var skipped = given === null || given === undefined || given === '';
    var cls = skipped ? 'xp-explain--skipped' : (ok ? 'xp-explain--right' : 'xp-explain--wrong');
    var head = skipped ? '⚑ Not answered — here is the reasoning'
      : (ok ? '✓ Correct' : '✗ Not correct');

    var correctText = q.type === 'entry'
      ? String(q.a)
      : 'ABCDEFGH'.charAt(q.a) + ' — ' + stripTags(q.opts[q.a]);

    var h = '<div class="xp-explain ' + cls + '">' +
      '<div class="xp-explain__head">' + head + '</div>' +
      '<div class="xp-explain__body">' +
      '<p><strong>Correct answer:</strong> ' + esc(correctText) + '</p>' +
      (q.why || '<p>No explanation was supplied for this item.</p>');

    if (q.visual) {
      h += '<figure class="xp-visual">' + q.visual +
        (q.caption ? '<figcaption>' + esc(q.caption) + '</figcaption>' : '') + '</figure>';
    }

    if (q.refs && q.refs.length) {
      h += '<div class="xp-refs"><h5>Go deeper</h5>';
      q.refs.forEach(function (r) {
        var icon = r.k === 'video' ? '▶' : (r.k === 'scholarly' ? '📄' : '🔗');
        var label = r.k === 'video' ? 'Video' : (r.k === 'scholarly' ? 'Scholarly' : 'Reference');
        h += '<a class="xp-ref" href="' + esc(r.u) + '" target="_blank" rel="noopener">' +
          '<span class="xp-ref__icon">' + icon + '</span><span>' + esc(r.t) + '</span>' +
          '<span class="xp-ref__type">' + label + '</span></a>';
      });
      h += '</div>';
    }

    h += '</div></div>';
    return h;
  }

  function stripTags(s) { return String(s).replace(/<[^>]*>/g, ''); }

  function isCorrect(q, given) {
    if (given === null || given === undefined || given === '') return false;
    if (q.type === 'entry') {
      var v = parseFloat(String(given).replace(/[^0-9.\-]/g, ''));
      if (isNaN(v)) return String(given).trim().toLowerCase() === String(q.a).trim().toLowerCase();
      var tol = q.tol === undefined ? 0.01 : q.tol;
      return Math.abs(v - parseFloat(q.a)) <= tol;
    }
    return given === q.a;
  }

  function refreshMeta() {
    var answered = SESSION.answers.filter(function (a) { return a !== null && a !== ''; }).length;
    var flagged = SESSION.flags.filter(Boolean).length;
    var pct = Math.round(answered / SESSION.total * 100);

    var set = function (id, v) { var n = $('#' + id); if (n) n.textContent = v; };
    set('xp-pos', (SESSION.idx + 1) + ' / ' + SESSION.total);
    set('xp-answered', answered);
    set('xp-flagged', flagged);
    set('xp-pctdone', pct + '%');
    var f1 = $('#xp-progressfill'), f2 = $('#xp-sidefill');
    if (f1) f1.style.width = pct + '%';
    if (f2) f2.style.width = pct + '%';

    var nav = $('#xp-nav');
    if (nav) {
      var h = '';
      for (var i = 0; i < SESSION.total; i++) {
        var cls = [];
        if (SESSION.answers[i] !== null && SESSION.answers[i] !== '') cls.push('is-answered');
        if (SESSION.flags[i]) cls.push('is-flagged');
        if (i === SESSION.idx) cls.push('is-current');
        if (SESSION.revealed[i]) cls.push(isCorrect(SESSION.paper[i], SESSION.answers[i]) ? 'is-right' : 'is-wrong');
        h += '<button class="' + cls.join(' ') + '" data-xp-goto="' + i + '" aria-label="Question ' + (i + 1) + '">' + (i + 1) + '</button>';
      }
      nav.innerHTML = h;
    }
    schedulePersist();
  }

  var _persistTimer = null;
  function schedulePersist() {
    clearTimeout(_persistTimer);
    _persistTimer = setTimeout(persistResume, 400);
  }

  function runnerClick(e) {
    var t0 = e.target;
    if (!t0 || typeof t0.closest !== 'function') return;

    /* Available with or without a live session — the results screen has no
       SESSION-dependent state left to protect. */
    if (t0.closest('[data-xp-redeem-here]')) {
      var code = window.prompt('Enter your premium unlock code:');
      if (code && SESSION) {
        var res = redeemCode(SESSION.examId, code);
        toast(res.msg, 5000);
      }
      return;
    }

    if (t0.closest('[data-xp-hub]')) {
      if (SESSION && !SESSION.finished) {
        if (!window.confirm('Leave this sitting? Your answers so far will not be graded or saved.')) return;
        clearInterval(TICK);
        document.removeEventListener('keydown', runnerKeys);
        store.patch(function (st) { st.resume = null; });
      }
      SESSION = null;
      showHubAgain();
      return;
    }

    if (!SESSION) return;
    var t = e.target;

    var opt = t.closest('[data-xp-opt]');
    if (opt && !opt.disabled) {
      SESSION.answers[SESSION.idx] = parseInt(opt.getAttribute('data-xp-opt'), 10);
      drawQuestion();
      return;
    }
    if (t.closest('[data-xp-flag]')) {
      SESSION.flags[SESSION.idx] = !SESSION.flags[SESSION.idx];
      drawQuestion(); return;
    }
    if (t.closest('[data-xp-check]')) { checkCurrent(); return; }
    if (t.closest('[data-xp-next]')) { go(SESSION.idx + 1); return; }
    if (t.closest('[data-xp-prev]')) { go(SESSION.idx - 1); return; }
    var goto = t.closest('[data-xp-goto]');
    if (goto) { go(parseInt(goto.getAttribute('data-xp-goto'), 10)); return; }
    if (t.closest('[data-xp-submit]')) { confirmFinish(false); return; }
    if (t.closest('[data-xp-end]')) { confirmFinish(true); return; }
    if (t.closest('[data-xp-retry]')) { location.reload(); return; }
    if (t.closest('[data-xp-print]')) { window.print(); return; }
  }

  function runnerKeys(e) {
    if (!SESSION) return;
    if (/^(INPUT|TEXTAREA)$/.test(document.activeElement.tagName)) {
      if (e.key === 'Enter') { e.preventDefault(); SESSION.mode === 'tutor' ? checkCurrent() : go(SESSION.idx + 1); }
      return;
    }
    var q = SESSION.paper[SESSION.idx];
    if (/^[1-8]$/.test(e.key) && q.opts && !SESSION.revealed[SESSION.idx]) {
      var k = parseInt(e.key, 10) - 1;
      if (k < q.opts.length) { SESSION.answers[SESSION.idx] = k; drawQuestion(); }
      return;
    }
    if (e.key === 'ArrowRight') { go(SESSION.idx + 1); }
    if (e.key === 'ArrowLeft') { go(SESSION.idx - 1); }
    if (e.key.toLowerCase() === 'f') { SESSION.flags[SESSION.idx] = !SESSION.flags[SESSION.idx]; drawQuestion(); }
    if (e.key === 'Enter' && SESSION.mode === 'tutor') { checkCurrent(); }
  }

  function checkCurrent() {
    var i = SESSION.idx;
    if (SESSION.revealed[i]) { go(i + 1); return; }
    SESSION.revealed[i] = true;
    var q = SESSION.paper[i];
    if (!isCorrect(q, SESSION.answers[i]) && !q.generated) {
      store.patch(function (s) {
        s.missed = s.missed || {};
        var m = s.missed[q.id] || { misses: 0, exam: q.exam };
        m.misses++; m.lastSeen = Date.now();
        s.missed[q.id] = m;
      });
    }
    drawQuestion();
  }

  function go(i) {
    if (!SESSION) return;
    if (i < 0) return;
    if (i >= SESSION.total) { confirmFinish(false); return; }
    SESSION.times[SESSION.idx] += Math.round((Date.now() - SESSION.qStart) / 1000);
    SESSION.qStart = Date.now();
    SESSION.idx = i;
    drawQuestion();
    var wrap = $('#xp-qwrap');
    if (wrap) wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function confirmFinish(early) {
    var unanswered = SESSION.answers.filter(function (a) { return a === null || a === ''; }).length;
    var msg = early
      ? 'End the sitting now?' + (unanswered ? '\n\n' + unanswered + ' question' + (unanswered === 1 ? '' : 's') + ' are unanswered. You will still get the full worked explanation for every one of them.' : '')
      : 'Submit the whole exam for grading?' + (unanswered ? '\n\n' + unanswered + ' question' + (unanswered === 1 ? '' : 's') + ' are still unanswered and will be marked incorrect — but you will get the explanations.' : '');
    if (confirm(msg)) finish(false);
  }

  /* Mastery bands. Deliberately conservative: a topic is only called
     "mastered" at 85% or better AND with enough questions behind it to mean
     something. Calling 3-for-3 mastery would be flattering and useless. */
  function band(pct, n) {
    if (n < 3) return { key: 'thin', label: 'Not enough data yet', colour: 'var(--xp-muted)' };
    if (pct >= 85) return { key: 'mastered', label: 'Mastered', colour: 'var(--xp-correct)' };
    if (pct >= 70) return { key: 'solid', label: 'Solid', colour: '#4a9d6e' };
    if (pct >= 50) return { key: 'developing', label: 'Developing', colour: 'var(--xp-warn)' };
    return { key: 'weak', label: 'Needs work', colour: 'var(--xp-wrong)' };
  }

  function masteryHTML(map, heading, note) {
    var rows = Object.keys(map).map(function (k) {
      var b = map[k], pct = b.n ? Math.round(b.ok / b.n * 100) : 0;
      return { name: k, pct: pct, n: b.n, ok: b.ok, band: band(pct, b.n) };
    }).sort(function (a, c) { return a.pct - c.pct; });

    if (!rows.length) return '';

    var strong = rows.filter(function (x) { return x.band.key === 'mastered' || x.band.key === 'solid'; });
    var weak = rows.filter(function (x) { return x.band.key === 'weak' || x.band.key === 'developing'; });

    var h = '<div class="xp-breakdown"><h3>' + esc(heading) + '</h3>';
    if (note) h += '<p style="font-size:13px;color:var(--xp-muted);margin:-4px 0 14px">' + note + '</p>';

    h += '<div class="xp-mastery">';
    rows.forEach(function (x) {
      h += '<div class="xp-mastery__row">' +
        '<div class="xp-mastery__head">' +
          '<span class="xp-mastery__name">' + esc(x.name) + '</span>' +
          '<span class="xp-mastery__tag" style="background:' + x.band.colour + '">' + x.band.label + '</span>' +
        '</div>' +
        '<div class="xp-meter__track"><div class="xp-meter__fill" style="width:' + x.pct + '%;background:' + x.band.colour + '"></div></div>' +
        '<div class="xp-mastery__foot">' + x.ok + ' of ' + x.n + ' correct \u2014 ' + x.pct + '%</div>' +
      '</div>';
    });
    h += '</div>';

    h += '<div class="xp-verdict">';
    if (strong.length) {
      h += '<div class="xp-verdict__col xp-verdict__col--good"><h4>\u2713 Strengths</h4><ul>' +
        strong.map(function (x) { return '<li><strong>' + esc(x.name) + '</strong> \u2014 ' + x.pct + '%' + (x.band.key === 'mastered' ? ' (mastered)' : '') + '</li>'; }).join('') +
        '</ul></div>';
    }
    if (weak.length) {
      h += '<div class="xp-verdict__col xp-verdict__col--bad"><h4>\u26a0 To improve</h4><ul>' +
        weak.map(function (x) { return '<li><strong>' + esc(x.name) + '</strong> \u2014 ' + x.pct + '%</li>'; }).join('') +
        '</ul></div>';
    }
    if (!strong.length && !weak.length) {
      h += '<p style="font-size:13px;color:var(--xp-muted)">Answer a few more questions in each section and the mastery breakdown will fill in.</p>';
    }
    h += '</div></div>';
    return h;
  }

  function finish(timeUp) {
    clearInterval(TICK);
    document.removeEventListener('keydown', runnerKeys);

    var correct = 0, bySection = {}, byDiff = {}, adds = [], clears = [], attempted = 0;
    SESSION.paper.forEach(function (q, i) {
      var ok = isCorrect(q, SESSION.answers[i]);
      if (SESSION.answers[i] !== null && SESSION.answers[i] !== '') attempted++;
      if (ok) correct++;
      var sn = sectionName(q) || 'General';
      bySection[sn] = bySection[sn] || { n: 0, ok: 0 };
      bySection[sn].n++; if (ok) bySection[sn].ok++;
      var d = q.difficulty || 'moderate';
      byDiff[d] = byDiff[d] || { n: 0, ok: 0 };
      byDiff[d].n++; if (ok) byDiff[d].ok++;
      if (!q.generated) (ok ? clears : adds).push(q);
    });

    /* one write, not one per question */
    store.patch(function (s) {
      s.missed = s.missed || {};
      adds.forEach(function (q) {
        var m = s.missed[q.id] || { misses: 0, exam: q.exam };
        m.misses++; m.lastSeen = Date.now();
        s.missed[q.id] = m;
      });
      clears.forEach(function (q) { delete s.missed[q.id]; });
    });

    var pct = Math.round(correct / SESSION.total * 100);
    var passed = pct >= SESSION.pass;

    store.patch(function (s) {
      s.history = s.history || [];
      s.history.push({
        examId: SESSION.examId, examName: SESSION.examName, at: Date.now(),
        correct: correct, total: SESSION.total, pct: pct, pass: SESSION.pass,
        attempted: attempted, skipped: SESSION.total - attempted,
        sections: bySection,
        difficulty: SESSION.difficulty, mode: SESSION.mode, seconds: SESSION.elapsed
      });
      /* Cumulative per-topic mastery, so strengths and weaknesses are judged
         across every sitting rather than from one paper. */
      s.mastery = s.mastery || {};
      var mk = s.mastery[SESSION.examId] || {};
      Object.keys(bySection).forEach(function (k) {
        var cur = mk[k] || { n: 0, ok: 0 };
        cur.n += bySection[k].n; cur.ok += bySection[k].ok;
        mk[k] = cur;
      });
      s.mastery[SESSION.examId] = mk;
      s.resume = null;
      /* streak */
      var today = new Date().toDateString();
      s.streak = s.streak || { count: 0, last: null };
      if (s.streak.last !== today) {
        var y = new Date(Date.now() - 86400000).toDateString();
        s.streak.count = (s.streak.last === y) ? (s.streak.count + 1) : 1;
        s.streak.last = today;
      }
    });

    SESSION.finished = true;
    renderResults({ correct: correct, pct: pct, passed: passed, bySection: bySection, byDiff: byDiff, timeUp: timeUp, attempted: attempted });
  }

  /* =====================================================================
     POST-RESULTS OFFERS
     Shown once grading is complete, when intent is highest and the student
     has just seen concrete evidence of where they are weak.
     ===================================================================== */
  var SHOP = window.XP_SHOP || {};
  var CONTACT_EMAIL = SHOP.email || 'Instanthelp24hr@gmail.com';
  var CONTACT_WA = SHOP.whatsapp || '17654709090';
  var CONTACT_WA_DISPLAY = SHOP.whatsappDisplay || '+1 (765) 470-9090';

  function waLink(msg) {
    return 'https://wa.me/' + CONTACT_WA + '?text=' + encodeURIComponent(msg);
  }
  function mailLink(subject, body) {
    return 'mailto:' + CONTACT_EMAIL + '?subject=' + encodeURIComponent(subject) +
      '&body=' + encodeURIComponent(body);
  }

  function sectionLabel(id) {
    var ex = findExam(SESSION && SESSION.examId);
    var hit = ((ex && ex.sections) || []).filter(function (x) { return x.id === id; })[0];
    return (hit && hit.name) || id;
  }

  function offersHTML(r) {
    var exam = findExam(SESSION.examId) || { name: 'this exam' };
    var nm = exam.name;
    var weak = r.weakestName ? r.weakestName : '';
    var scoreLine = 'I just scored ' + r.pct + '% on the ' + nm + ' practice test on your site.'
      + (weak ? ' My weakest area was ' + weak + '.' : '');

    var h = '<div class="xp-offers">';

    /* ---- 1. Tutor / class assistance ---- */
    h += '<div class="xp-offer xp-offer--help">' +
      '<div class="xp-offer__badge">Recommended for you</div>' +
      '<h3>Want a human expert on this?</h3>' +
      '<p class="xp-offer__lede">You have just seen exactly where the gaps are. ' +
        'Our tutors work on those specific areas rather than starting from scratch' +
        (weak ? ' \u2014 starting with <strong>' + esc(weak) + '</strong>' : '') + '.</p>' +

      '<div class="xp-offer__grid">' +
        '<div class="xp-offer__card">' +
          '<span class="xp-offer__icon" aria-hidden="true">\u{1F393}</span>' +
          '<h4>Professor-Led Proctored Exam Assistance</h4>' +
          '<p>Professor-led preparation and live support for ' + esc(nm) + ' and other proctored exams.</p>' +
          '<div class="xp-offer__btns">' +
            '<a class="xp-btn xp-btn--wa" href="' + waLink('Hi, I need Tutor Assistance with a Proctored Exam. ' + scoreLine) + '" target="_blank" rel="noopener">' +
              '<span aria-hidden="true">\u{1F4AC}</span> WhatsApp Quick Chat</a>' +
            '<a class="xp-btn xp-btn--ghost" href="' + mailLink('Professor-Led Proctored Exam Assistance \u2014 ' + nm, scoreLine + '\n\nPlease tell me about tutor assistance for this proctored exam.\n\nMy name:\nExam date:\nSchool:') + '">' +
              '<span aria-hidden="true">\u2709</span> Email us</a>' +
          '</div>' +
        '</div>' +

        '<div class="xp-offer__card">' +
          '<span class="xp-offer__icon" aria-hidden="true">\u{1F4DA}</span>' +
          '<h4>Full Nursing Course Assistance</h4>' +
          '<p>A nursing professor handles the full course \u2014 lectures, discussions, quizzes and exams \u2014 for the whole term.</p>' +
          '<div class="xp-offer__btns">' +
            '<a class="xp-btn xp-btn--wa" href="' + waLink('Hi, I am interested in Full Nursing Course Assistance. ' + scoreLine) + '" target="_blank" rel="noopener">' +
              '<span aria-hidden="true">\u{1F4AC}</span> WhatsApp Quick Chat</a>' +
            '<a class="xp-btn xp-btn--gold" href="/pages/order-now?service=onlineclass">Reserve an expert \u2192</a>' +
          '</div>' +
        '</div>' +
      '</div>' +

      '<p class="xp-offer__contact">' +
        '<strong>Email:</strong> <a href="' + mailLink('Help with ' + nm, scoreLine) + '">' + esc(CONTACT_EMAIL) + '</a>' +
        ' &nbsp;&bull;&nbsp; ' +
        '<strong>WhatsApp Quick Chat:</strong> <a href="' + waLink(scoreLine) + '" target="_blank" rel="noopener">' + esc(CONTACT_WA_DISPLAY) + '</a>' +
      '</p>' +
    '</div>';

    /* ---- 2. Premium real-exam question bank ---- */
    var unlocked = (state().unlocks || {})[SESSION.examId];
    h += '<div class="xp-offer xp-offer--premium">' +
      '<div class="xp-offer__ribbon">Premium \u2014 $' + LIM.premiumPrice + '</div>' +
      '<span class="xp-offer__icon xp-offer__icon--lg" aria-hidden="true">\u{1F513}</span>' +
      '<h3>Unlock the full ' + esc(nm) + ' exam package</h3>' +
      '<ul class="xp-offer__list">' +
        '<li>Practise up to <strong>' + LIM.premiumMax + ' questions</strong> per sitting instead of ' + LIM.freeMax + '</li>' +
        '<li>Extended question bank built to mirror the <strong>real exam environment</strong></li>' +
        '<li>Full-length timed papers with the real section weighting</li>' +
        '<li>Worked explanations, diagrams and references on every question</li>' +
      '</ul>' +
      (unlocked
        ? '<p class="xp-offer__done">\u2713 Already unlocked on this browser \u2014 your limit is ' + unlocked.cap + ' questions.</p>'
        : '<div class="xp-offer__btns xp-offer__btns--center">' +
            '<a class="xp-btn xp-btn--buy" href="/products/' + esc(LIM.premiumHandle) + '">Get the $' + LIM.premiumPrice + ' exam package</a>' +
            '<button class="xp-btn xp-btn--ghost" data-xp-redeem-here>I already have a code</button>' +
          '</div>' +
          '<p class="xp-offer__fine">Released after admin approval. You get an unlock code by email, usually within the hour.</p>') +
    '</div>';

    /* ---- 3. Assignment help reminder ---- */
    h += '<div class="xp-offer xp-offer--assign">' +
      '<span class="xp-offer__icon" aria-hidden="true">\u270D</span>' +
      '<div>' +
        '<h4>Need Assignment Help?</h4>' +
        '<p><strong>Human nursing writers for AI-free assignment help</strong> \u2014 care plans, essays, ' +
          'research papers, dissertations, capstone projects and PhD theses, written from scratch by a nursing subject expert. No AI-generated text.</p>' +
        '<div class="xp-offer__btns">' +
          '<a class="xp-btn xp-btn--primary" href="/pages/order-now?service=writing">Get assignment help</a>' +
          '<a class="xp-btn xp-btn--wa" href="' + waLink('Hi, I need Assignment Help from a human tutor.') + '" target="_blank" rel="noopener">' +
            '<span aria-hidden="true">\u{1F4AC}</span> WhatsApp</a>' +
        '</div>' +
      '</div>' +
    '</div>';

    h += '</div>';
    return h;
  }

  /* A modal that appears once, immediately after grading. */
  function showResultsOffer(r) {
    var exam = findExam(SESSION.examId) || { name: 'this exam' };
    var nm = exam.name;
    var weak = r.weakestName || '';
    var scoreLine = 'I just scored ' + r.pct + '% on the ' + nm + ' practice test on your site.'
      + (weak ? ' My weakest area was ' + weak + '.' : '');

    var old = $('#xp-offer-modal');
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var m = el('div', 'xp-modal xp-modal--offer');
    m.id = 'xp-offer-modal';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.innerHTML =
      '<div class="xp-modal__box xp-offer-pop">' +
        '<button class="xp-modal__x" data-xp-offer-close aria-label="Close">&times;</button>' +
        '<div class="xp-offer-pop__head">' +
          '<span class="xp-offer-pop__score">' + r.pct + '%</span>' +
          '<div>' +
            '<h3>' + (r.passed ? 'Well done \u2014 that is a pass.' : 'Good effort. Let us close the gap.') + '</h3>' +
            '<p>' + (weak
              ? 'Your weakest area was <strong>' + esc(weak) + '</strong>. A tutor can work on exactly that.'
              : 'A human expert can take it from here.') + '</p>' +
          '</div>' +
        '</div>' +

        '<div class="xp-offer-pop__opts">' +
          '<a class="xp-offer-pop__opt" href="' + waLink('Hi, I need Tutor Assistance with a Proctored Exam. ' + scoreLine) + '" target="_blank" rel="noopener">' +
            '<span class="xp-offer__icon" aria-hidden="true">\u{1F393}</span>' +
            '<strong>Professor-Led Proctored Exam Assistance</strong>' +
            '<span>Live one-on-one help for ' + esc(nm) + '</span>' +
          '</a>' +
          '<a class="xp-offer-pop__opt" href="/pages/order-now?service=onlineclass">' +
            '<span class="xp-offer__icon" aria-hidden="true">\u{1F4DA}</span>' +
            '<strong>Full Nursing Course Assistance</strong>' +
            '<span>A specialist handles the whole course</span>' +
          '</a>' +
        '</div>' +

        '<div class="xp-offer-pop__contact">' +
          '<a class="xp-btn xp-btn--wa" href="' + waLink(scoreLine) + '" target="_blank" rel="noopener">' +
            '<span aria-hidden="true">\u{1F4AC}</span> WhatsApp: ' + esc(CONTACT_WA_DISPLAY) + '</a>' +
          '<a class="xp-btn xp-btn--ghost" href="' + mailLink('Help with ' + nm, scoreLine) + '">' +
            '<span aria-hidden="true">\u2709</span> ' + esc(CONTACT_EMAIL) + '</a>' +
        '</div>' +

        '<button class="xp-offer-pop__dismiss" data-xp-offer-close>See my full results first</button>' +
      '</div>';

    document.body.appendChild(m);
    setTimeout(function () { m.classList.add('is-open'); }, 60);

    m.addEventListener('click', function (e) {
      if (e.target === m || (e.target.closest && e.target.closest('[data-xp-offer-close]'))) {
        m.classList.remove('is-open');
        setTimeout(function () { if (m.parentNode) m.parentNode.removeChild(m); }, 240);
      }
    });
  }

  function renderResults(r) {
    var root = runnerRoot();
    var circ = 2 * Math.PI * 62;
    var offset = circ * (1 - r.pct / 100);

    var h = '<div class="xp-results">' +
      '<div class="xp-score">' +
        (r.timeUp ? '<p style="color:#ffb4ad;font-weight:800;margin-bottom:10px">⏱ Time expired — the paper was submitted automatically.</p>' : '') +
        '<div class="xp-score__ring">' +
          '<svg width="148" height="148" viewBox="0 0 148 148">' +
            '<circle class="xp-ring-bg" cx="74" cy="74" r="62"></circle>' +
            '<circle class="xp-ring-fg" cx="74" cy="74" r="62" stroke-dasharray="' + circ + '" stroke-dashoffset="' + circ + '" id="xp-ring"></circle>' +
          '</svg>' +
          '<div class="xp-score__pct"><b>' + r.pct + '%</b><small>' + r.correct + ' / ' + SESSION.total + '</small></div>' +
        '</div>' +
        '<h2>' + esc(SESSION.examName) + '</h2>' +
        '<p>' + SESSION.total + ' questions · ' + esc(SESSION.difficulty) + ' · ' + (SESSION.mode === 'exam' ? 'real exam mode' : 'tutor mode') + ' · ' + fmtTime(SESSION.elapsed) + '</p>' +
        '<span class="xp-verdict ' + (r.passed ? 'xp-verdict--pass' : 'xp-verdict--fail') + '">' +
          (r.passed ? '✓ Above the ' + SESSION.pass + '% benchmark' : 'Below the ' + SESSION.pass + '% benchmark') + '</span>' +
      '</div>';

    /* stats */
    var answered = SESSION.answers.filter(function (a) { return a !== null && a !== ''; }).length;
    h += '<div class="xp-stat-grid">' +
      '<div class="xp-stat"><b>' + r.correct + '</b><span>Correct</span></div>' +
      '<div class="xp-stat"><b>' + (answered - r.correct) + '</b><span>Incorrect</span></div>' +
      '<div class="xp-stat"><b>' + (SESSION.total - answered) + '</b><span>Unanswered</span></div>' +
      '<div class="xp-stat"><b>' + (SESSION.total ? Math.round(SESSION.elapsed / SESSION.total) : 0) + 's</b><span>Avg per question</span></div>' +
      '</div>';

    /* This sitting, topic by topic */
    var weakest = null;
    Object.keys(r.bySection).forEach(function (k) {
      var b = r.bySection[k], p = Math.round(b.ok / b.n * 100);
      if (!weakest || p < weakest.p) weakest = { name: k, p: p };
    });
    r.weakestName = weakest ? sectionLabel(weakest.name) : '';
    h += masteryHTML(r.bySection, 'This sitting \u2014 topic by topic',
      'Mastered is 85% or better. A topic needs at least 3 questions before it is graded at all.');

    /* Cumulative across every sitting on this track */
    var mst = state().mastery || {};
    var cum = mst[SESSION.examId];
    if (cum && Object.keys(cum).length) {
      var totalSeen = 0;
      Object.keys(cum).forEach(function (k) { totalSeen += cum[k].n; });
      if (totalSeen > SESSION.total) {
        h += masteryHTML(cum, 'All your sittings on this track combined',
          'Based on ' + totalSeen + ' questions across every attempt. This is the more reliable picture \u2014 one paper is a small sample.');
      }
    }

    h += '<div class="xp-breakdown"><h3>By difficulty</h3>';
    ['easy', 'moderate', 'hard', 'advanced'].forEach(function (d) {
      var b = r.byDiff[d]; if (!b) return;
      var p = Math.round(b.ok / b.n * 100);
      h += '<div class="xp-meter"><div class="xp-meter__top"><span style="text-transform:capitalize">' + d + '</span><b>' + b.ok + '/' + b.n + ' — ' + p + '%</b></div>' +
        '<div class="xp-meter__track"><div class="xp-meter__fill" style="width:' + p + '%"></div></div></div>';
    });
    h += '</div>';

    if (weakest && weakest.p < 100) {
      h += '<div class="xp-premium-note" style="margin-bottom:24px">' +
        '<strong>Where to spend your next session:</strong> ' + esc(weakest.name) + ' came in at ' + weakest.p + '%. ' +
        'Rerun this track with only that section selected and difficulty set to <em>Hard</em> — narrow, repeated exposure moves a weak section faster than another mixed paper.</div>';
    }

    /* controls */
    h += '<div class="xp-controls" style="border:none;margin-bottom:24px">' +
      '<button class="xp-btn xp-btn--gold" data-xp-retry>Practise again</button>' +
      '<button class="xp-btn xp-btn--ghost" data-xp-print>Print / save as PDF</button>' +
      '<a class="xp-btn xp-btn--primary" href="/pages/contact">Get 1-on-1 help from a nursing professor</a>' +
      '<button class="xp-btn xp-btn--ghost" data-xp-hub>Back to all exams</button>' +
      '</div>';

    /* offers — placed after the analysis, before the long answer review, so
       they sit where attention still is */
    h += offersHTML(r);
    h += relatedTagsHTML(SESSION.examId);

    /* full review — every question, answered or not */
    h += '<h3 style="font-family:var(--font-heading);font-size:22px;color:var(--color-primary);margin:30px 0 16px">Full answer review</h3>' +
      '<p style="font-size:14px;color:var(--xp-muted);margin-bottom:18px">Every question is below with its worked explanation — including the ones you skipped.</p>';

    SESSION.paper.forEach(function (q, i) {
      var given = SESSION.answers[i];
      var skipped = given === null || given === '';
      var ok = isCorrect(q, given);
      var cls = skipped ? 'is-skipped' : (ok ? 'is-right' : 'is-wrong');
      h += '<div class="xp-review-item ' + cls + '">' +
        '<div class="xp-q__head"><span class="xp-q__num">Question ' + (i + 1) + '</span>' +
        '<span class="xp-diff xp-diff--' + esc(q.difficulty || 'moderate') + '">' + esc(q.difficulty || 'moderate') + '</span>' +
        '<span class="xp-tag">' + esc(sectionName(q)) + '</span></div>' +
        (q.stimulus ? '<details style="margin-bottom:12px"><summary style="cursor:pointer;font-size:13px;font-weight:700;color:var(--color-primary)">Show the passage</summary><div class="xp-stimulus" style="margin-top:10px">' + q.stimulus + '</div></details>' : '') +
        '<div class="xp-q__text" style="font-size:15.5px">' + q.q + '</div>';

      if (q.opts) {
        h += '<div class="xp-answers" style="margin-bottom:6px">';
        q.opts.forEach(function (o, k) {
          var c = 'xp-answer is-locked';
          var mk = '';
          if (k === q.a) { c += ' is-correct'; mk = '<span class="xp-answer__mark">✓</span>'; }
          else if (k === given) { c += ' is-wrong'; mk = '<span class="xp-answer__mark">✗ you</span>'; }
          h += '<div class="' + c + '"><span class="xp-answer__key">' + 'ABCDEFGH'.charAt(k) + '</span><span>' + o + '</span>' + mk + '</div>';
        });
        h += '</div>';
      } else {
        h += '<p style="font-size:14px;margin-bottom:8px">Your answer: <strong>' + (skipped ? '(none)' : esc(given)) + '</strong></p>';
      }

      h += explanationHTML(q, given) + '</div>';
    });

    h += '</div>';
    root.innerHTML = h;
    root.addEventListener('click', runnerClick);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(function () {
      var ring = $('#xp-ring');
      if (ring) ring.style.strokeDashoffset = offset;
    }, 120);

    /* Offer modal, once per sitting, after the score has registered. */
    setTimeout(function () { try { showResultsOffer(r); } catch (e) {} }, 1400);
  }

  /* =====================================================================
     BOOT
     ===================================================================== */
  /* Last line of defence. Without this an exception anywhere leaves a blank
     page and no clue; with it the user sees what failed and can report it. */
  function installErrorNet() {
    window.addEventListener('error', function (e) {
      if (!e || !e.filename || e.filename.indexOf('exam-') === -1) return;
      if ($('.xp-fatal')) return;
      showFatal('The practice platform hit an error.',
        (e.message || 'Unknown') + '  (' + (e.filename || '').split('/').pop() + ':' + (e.lineno || '?') + ')');
    });
  }

  function boot() {
    installErrorNet();

    /* Fail loudly if a dependency did not load — previously this produced an
       empty hub with no explanation. */
    if (!CAT.length) {
      var h0 = $('#xp-hub');
      if (h0) {
        h0.innerHTML = '<div class="xp-section"><div class="xp-fatal">' +
          '<h3>The practice platform did not finish loading</h3>' +
          '<p>The exam catalogue script did not load. This is usually a missing theme asset. ' +
          'Check that <code>exam-catalog.js</code>, <code>exam-bank.js</code> and ' +
          '<code>exam-generator.js</code> are all present in the theme\u2019s assets folder.</p>' +
          '</div></div>';
      }
      return;
    }
    /* sticky order bar dismiss */
    var closeBar = $('#order-bar-close');
    if (closeBar) {
      closeBar.addEventListener('click', function () {
        var bar = $('#order-bar');
        if (bar) bar.classList.add('is-dismissed');
        try { sessionStorage.setItem('tmoc_orderbar_hidden', '1'); } catch (e) {}
      });
      try {
        if (sessionStorage.getItem('tmoc_orderbar_hidden') === '1') {
          var b = $('#order-bar'); if (b) b.classList.add('is-dismissed');
        }
      } catch (e) {}
    }

    var hub = $('#xp-hub');
    if (hub) renderHub(hub);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* expose for debugging / future extension */
  window.XP = {
    openSetup: openSetup, state: state, redeemCode: redeemCode,
    version: '1.0.0'
  };
})();
